import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { CustomSearchCriteria, PageNumbers } from '@catalog/const';
import { ProductListFacade } from '@catalog/data-access';
import { CityService } from '@city/feature';
import { CustomProduct } from '@product/const';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ActiveFacetsService, SearchFacetService } from '@search/feature';
import { ProductSearchPage } from '@spartacus/core';
import { Observable, combineLatest } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ProductListService {
  lastSearchBaseStoreCode: string;

  constructor(
    private productListFacade: ProductListFacade,
    private activeFacetsService: ActiveFacetsService,
    private cityService: CityService,
  ) {}

  /**
   * Если мы заинитились с SSR или поиск завершен (дубликат), лишний раз дергать search не нужно
   */
  distinctSearch(criteria: CustomSearchCriteria): Observable<ProductSearchPage> {
    return this.productListFacade.getLoading().pipe(
      filter((loading) => !loading),
      switchMap(() =>
        combineLatest({
          results: this.productListFacade.getResults(),
          baseStoreCode: this.cityService.activeBaseStoreCode$,
        }),
      ),
      take(1),
      tap(({ results, baseStoreCode }) => {
        const query: any = results.currentQuery?.query || {};

        if (
          baseStoreCode !== this.lastSearchBaseStoreCode ||
          !results ||
          query.facets !== criteria.facets ||
          query.freeTextSearch !== criteria.freeTextSearch ||
          (criteria.sort && criteria.sort !== query.sort)
        ) {
          this.lastSearchBaseStoreCode = baseStoreCode;
          this.search(criteria);
        }
      }),
      map(({ results }) => results),
    );
  }

  search(criteria: CustomSearchCriteria): void {
    const currentPage = criteria.currentPage;
    const pageSize = criteria.pageSize;
    const sort = criteria.sort;
    const freeTextSearch = criteria.freeTextSearch || '';
    const facets = criteria.facets || null;
    const isSearchPage = Boolean(criteria.freeTextSearch && pageSize > 1);

    this.productListFacade.search(
      freeTextSearch,
      this.filterDefinedProperties({
        currentPage,
        pageSize,
        sort,
        freeTextSearch,
        facets,
        enrich: isSearchPage ? 10 : undefined,
        referenceType: isSearchPage ? 'SIMILAR' : undefined,
      }),
    );
  }

  getProductListFacets(semanticRoute: string, params: Params): Observable<string> {
    return this.activeFacetsService.paramsFromRouteState(semanticRoute, params).pipe(
      map((facetParams) => {
        return SearchFacetService.createSearchQuery(facetParams);
      }),
    );
  }

  getSelectedSort(): Observable<string> {
    return this.productListFacade.getResults().pipe(
      filter<ProductSearchPage>((results) => Boolean(results?.sorts)),
      map((results) => results.sorts.find(({ selected }) => selected)?.code),
    );
  }

  getFreeTextSearch(): Observable<string> {
    return this.productListFacade.getResults().pipe(
      filter<ProductSearchPage>((results) => Boolean(results)),
      map(({ freeTextSearch }) => freeTextSearch),
    );
  }

  getNextQuery(): Observable<string> {
    return this.activeFacetsService.getSelectedFacetParams().pipe(
      take(1),
      map((searchFacetParams) => SearchFacetService.createSearchQuery(searchFacetParams)),
    );
  }

  getLoading(): Observable<boolean> {
    return this.productListFacade.getLoading();
  }

  getMoreLoading(): Observable<boolean> {
    return this.productListFacade.getMoreLoading();
  }

  getProductsByCodes(productCodes: string[], scope: string): Observable<CustomProduct[]> {
    return this.productListFacade.getProductsByCodes(productCodes, scope);
  }

  getPageNumbers(): Observable<PageNumbers> {
    return this.productListFacade.getPageNumbers();
  }

  resetPageNumbers(): void {
    this.productListFacade.resetPageNumbers();
  }

  loadMore(criteria: CustomSearchCriteria): void {
    this.productListFacade.loadMore(criteria);
  }

  getResults(): Observable<ProductSearchPage> {
    return this.productListFacade.getResults();
  }

  loadProductsByCodes(productCodes: string[], scope: string): void {
    return this.productListFacade.loadProductsByCodes(productCodes, scope);
  }

  getSnapshotProductsByCodes(productCodes: string[], scope: string): Observable<CustomProduct[]> {
    return this.productListFacade.getSnapshotProductsByCodes(productCodes, scope);
  }

  private filterDefinedProperties<T extends object>(obj: T): T {
    return Object.fromEntries(Object.entries(obj).filter(([, value]) => value !== null && value !== undefined)) as T;
  }
}
